
import React, { useState } from 'react';
import { useData } from '../../contexts/DataContext';
import { UserRole } from '../../types';
import { SEOHead } from '../../components/seo/SEOHead';
import { Breadcrumb } from '../../components/seo/Breadcrumb';
import { Card } from '../../components/cards/Card';
import { Button } from '../../components/buttons/Button';
import { Badge } from '../../components/common/Badge';
import { Search, Briefcase, Phone, MessageCircle, ShieldCheck, User } from 'lucide-react';

export const Agents: React.FC = () => {
    const { users, properties } = useData();
    const [searchTerm, setSearchTerm] = useState('');

    // 1. Filter for Agents and Admins (who act as agents)
    // 1. Filter for Agents and Admins (who act as agents)
    const agents = users.filter(u => {
        // Status check: Include both ACTIVE and APPROVED users
        const isProductiveStatus = u.status === 'ACTIVE' || u.status === 'APPROVED';
        if (!isProductiveStatus) return false;

        // Property check: Must have at least one property (system listing or manual count)
        const systemCount = properties.filter(p => p.agentId === u.id && p.status === 'AVAILABLE').length;
        const manualCount = parseInt(u.propertyCount || '0') || 0;
        const hasListings = (systemCount + manualCount) > 0;

        if (u.role === UserRole.AGENT) {
            // Include Agents only if they have listings (as per user request: "if they don't have a property don't display them")
            return hasListings;
        }

        if (u.role === UserRole.ADMIN) {
            // Include Admins only if they have listings
            return hasListings;
        }

        return false;
    });

    // 2. Filter by Search
    const filteredAgents = agents.filter(agent => {
        const term = searchTerm.toLowerCase();
        return (
            agent.name.toLowerCase().includes(term) ||
            (agent.agencyName && agent.agencyName.toLowerCase().includes(term))
        );
    });

    return (
        <>
            <SEOHead
                title="Find Student Accommodation Agents | IndigoBeans Rooms"
                description="Connect with verified agents managing student accommodation. Browse profiles and contact them directly via WhatsApp."
                canonical="https://rooms.indigobeans.co.za/agents"
            />

            <div className="min-h-screen bg-slate-50 font-sans pb-12">
                {/* Breadcrumb */}
                <div className="bg-white border-b border-gray-100">
                    <div className="max-w-7xl mx-auto px-4 py-2">
                        <Breadcrumb items={[{ name: 'Agents', url: '/agents' }]} />
                    </div>
                </div>

                {/* Header Section */}
                <section className="bg-[#2f5c96] text-white py-16 relative overflow-hidden">
                    {/* Decorative BG */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3"></div>
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -translate-x-1/3 translate-y-1/3"></div>

                    <div className="max-w-7xl mx-auto px-4 relative z-10 text-center">
                        <h1 className="text-4xl font-extrabold tracking-tight mb-4">Meet Our Agents</h1>
                        <p className="text-blue-100 max-w-2xl mx-auto text-lg">
                            Verified professionals helping you find your perfect student home.
                        </p>
                    </div>
                </section>

                {/* Search & List Section */}
                <div className="max-w-7xl mx-auto px-4 -mt-8 relative z-20">
                    {/* Search Bar */}
                    <Card className="p-4 mb-8 shadow-lg flex items-center gap-4 max-w-3xl mx-auto">
                        <Search className="text-gray-400 w-5 h-5 flex-shrink-0" />
                        <input
                            type="text"
                            placeholder="Search by name or agency..."
                            className="w-full outline-none text-gray-700 placeholder-gray-400"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </Card>

                    {/* Agents Grid */}
                    {filteredAgents.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredAgents.map(agent => {
                                const systemCount = properties.filter(p => p.agentId === agent.id && p.status === 'AVAILABLE').length;
                                const manualCount = parseInt(agent.propertyCount || '0') || 0;
                                const totalListings = systemCount + manualCount;

                                return (
                                    <Card key={agent.id} className="overflow-hidden hover:shadow-md transition-shadow flex flex-col h-full">
                                        <div className="p-6 flex flex-col items-center flex-grow text-center">
                                            {/* Avatar */}
                                            <div className="w-24 h-24 rounded-full bg-slate-100 border-4 border-white shadow-sm mb-4 overflow-hidden relative">
                                                {agent.avatar ? (
                                                    <img src={agent.avatar} alt={agent.name} className="w-full h-full object-cover" />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center text-slate-400">
                                                        <User className="w-10 h-10" />
                                                    </div>
                                                )}
                                                {(agent.isTrusted || agent.role === UserRole.ADMIN) && (
                                                    <div className="absolute bottom-0 right-0 bg-white rounded-full p-1 shadow-sm" title={agent.role === UserRole.ADMIN ? "Official Agent" : "Verified Agent"}>
                                                        <ShieldCheck className={`w-5 h-5 ${agent.role === UserRole.ADMIN ? "text-purple-600 fill-purple-100" : "text-[#2f5c96] fill-blue-100"}`} />
                                                    </div>
                                                )}
                                            </div>

                                            {/* Details */}
                                            <h3 className="text-xl font-bold text-gray-900 mb-1">{agent.name}</h3>
                                            {agent.agencyName && (
                                                <div className="flex items-center gap-1 text-sm text-[#2f5c96] font-medium mb-3">
                                                    <Briefcase className="w-3 h-3" />
                                                    {agent.agencyName}
                                                </div>
                                            )}

                                            <div className="mt-auto w-full pt-4 space-y-3">
                                                {/* Stats (Optional) */}
                                                <div className="grid grid-cols-2 gap-2 text-xs text-gray-500 border-t border-b border-gray-100 py-3 mb-4">
                                                    <div>
                                                        <span className="block font-bold text-gray-800 text-sm">{totalListings}</span>
                                                        Active Listings
                                                    </div>
                                                    <div>
                                                        <span className="block font-bold text-gray-800 text-sm">{new Date(agent.joinedDate || Date.now()).getFullYear()}</span>
                                                        Member Since
                                                    </div>
                                                </div>

                                                {/* WhatsApp Button */}
                                                {agent.phoneNumber ? (
                                                    <a
                                                        href={`https://wa.me/${agent.phoneNumber.replace(/[^0-9]/g, '')}?text=Hi ${agent.name}, I found your profile on Indigo Beans Rooms and interested in your listings.`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="w-full"
                                                    >
                                                        <Button className="w-full bg-green-600 hover:bg-green-700 border-none">
                                                            <MessageCircle className="w-4 h-4 mr-2" />
                                                            WhatsApp Agent
                                                        </Button>
                                                    </a>
                                                ) : (
                                                    <Button disabled variant="outline" className="w-full opacity-50 cursor-not-allowed">
                                                        <Phone className="w-4 h-4 mr-2" /> No Contact Info
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </Card>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-24">
                            <div className="bg-slate-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Briefcase className="w-8 h-8 text-slate-400" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-2">No agents found</h3>
                            <p className="text-gray-500 max-w-sm mx-auto">
                                We couldn't find any agents matching your search. Try different keywords or browse our accommodation directly.
                            </p>
                            <Button variant="outline" onClick={() => setSearchTerm('')} className="mt-6">Clear Search</Button>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};
